import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AdminRolesEnum } from '../shared/enums/admin-roles.enum';
import { AuthService } from '../shared/service/auth.service';
import { EconomicalGroupService } from './services/economical-group.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { EconomicalGroupModel } from '../shared/models/economical-group.model';
import { LoadingService } from '../shared/service/loading.service';
import { FdFieldConfigs } from '../shared/fd-form-components/fd-form-components.module';
import { AddEconomicalGroupComponent } from './add-economical-group/add-economical-group.component';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { catchError, map } from 'rxjs/operators';
import { FdAlertComponent, ModalDefinitions } from '../shared/fd-form-components/fd-alert/fd-alert.component';
import { Messages } from '../shared/messages/messages';
import { SearchEconomicalGroupModel } from './models/search-economical-group.model';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { EconomicalGroupDetailModel } from '../shared/models/economical-group-detail.model';

@Component({
    selector: 'app-economical-group',
    templateUrl: './economical-group.component.html',
    styleUrls: ['./economical-group.component.scss'],
    standalone: false
})
export class EconomicalGroupComponent implements OnInit {

  formGroup: UntypedFormGroup;
  fields: FdFieldConfigs;
  dataSource = new MatTableDataSource<EconomicalGroupModel>();

  pageNumber = 0;
  size = 10;
  totalItens = 0;
  pageNumberFilter = 0;

  selectedInstitution: string;
  icons: { [key: string]: IconDefinition }

  constructor(private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private groupService: EconomicalGroupService,
    private loadingService: LoadingService,
    private modal: MatDialog) { 
      this.icons = { faPlusCircle }
    }

  ngOnInit() {
    this.startForms();
  }

  changePage(event: any) {
    this.size = event.pageSize;
    this.pageNumber = event.pageIndex;
    this.getAllGroups(true);
  }

  getAllGroups(searchWithPageNumber: boolean = false) {
    this.loadingService.show();

    this.pageNumberFilter = searchWithPageNumber ? this.pageNumber : 0;

    const filtro = new SearchEconomicalGroupModel();
    filtro.institution = this.formGroup.value.institution;
    filtro.serviceContract = this.formGroup.value.serviceContract;
    filtro.size = this.size.toString();
    filtro.pageNumber = this.pageNumberFilter.toString();

    this.groupService.findEconomicalGroupByServiceContractPageable(filtro)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe((pageable) => {

        if (pageable) {
          const groups = (pageable.response || []).map((group: any) => ({
            ...group,
            haveCampaign: this.resolveCampaignFlag(group)
          }));

          this.enrichCampaignFlagsWhenMissing(groups);
          this.totalItens = pageable.totalItens;
          this.pageNumber = pageable.page;
          this.size = pageable.size;
        }
      }, error => {

        const dialogRef = this.modal.open(FdAlertComponent, {
          disableClose: true,
          width: ModalDefinitions.DEFAULT_MODAL_WIDTH,
          data: Messages.SEARCH_ERROR
        });
      });
  }

  private enrichCampaignFlagsWhenMissing(groups: EconomicalGroupModel[]): void {
    const missingGroups = groups.filter(g => !this.hasCampaignField(g));
    
    if (missingGroups.length === 0) {
      this.dataSource.data = groups;
      return;
    }

    // Batch fetch details for groups missing campaign flag
    const detailRequests: { group: EconomicalGroupModel; request: Observable<boolean> }[] = 
      groups.map(group => ({
        group,
        request: this.hasCampaignField(group)
          ? of(Boolean(group.haveCampaign))
          : this.groupService.getDetailsGroup(group.idEconomicGroup, group.serviceContract)
              .pipe(
                map((detail: EconomicalGroupDetailModel) => (detail?.campaigns || []).length > 0),
                catchError(() => of(false)) // Default to false on error
              )
      }));

    forkJoin(detailRequests.map(r => r.request)).subscribe((resolvedCampaignFlags: boolean[]) => {
      this.dataSource.data = groups.map((group, index) => ({
        ...group,
        haveCampaign: resolvedCampaignFlags[index]
      }));
    });
  }

  /**
   * Checks if group has explicit campaign field in any naming variant.
   * Handles different API response formats and naming conventions.
   */
  private hasCampaignField(group: any): boolean {
    return (group?.haveCampaign !== undefined && group?.haveCampaign !== null)
      || (group?.hasCampaign !== undefined && group?.hasCampaign !== null)
      || (group?.have_campaign !== undefined && group?.have_campaign !== null)
      || (group?.has_campaign !== undefined && group?.has_campaign !== null);
  }

  /**
   * Resolves campaign eligibility flag using fallback chain:
   * 1. Explicit boolean field (handles string 'true'/'false' from APIs)
   * 2. Campaigns array length
   * 3. Campaign count fields
   * 4. Default to false
   * 
   * This flexibility ensures compatibility with multiple backend versions.
   */
  private resolveCampaignFlag(group: any): boolean {
    const explicitFlag = group?.haveCampaign ?? group?.hasCampaign ?? group?.have_campaign ?? group?.has_campaign;
    if (explicitFlag !== undefined && explicitFlag !== null) {
      if (typeof explicitFlag === 'string') {
        const normalized = explicitFlag.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
      }
      return Boolean(explicitFlag);
    }

    const campaigns = group?.campaigns ?? group?.campaignList;
    if (Array.isArray(campaigns)) {
      return campaigns.length > 0;
    }

    const campaignsCount = group?.campaignsCount ?? group?.campaignCount ?? group?.qtyCampaigns;
    if (typeof campaignsCount === 'number') {
      return campaignsCount > 0;
    }
    if (typeof campaignsCount === 'string') {
      const parsed = Number(campaignsCount);
      return !Number.isNaN(parsed) && parsed > 0;
    }

    return false;
  }

  hasWriteAccess() {
    return this.authService.isUserInRoles([AdminRolesEnum.PRICING_WRITE]);
  }

  startForms(): void {

    this.formGroup = this.formBuilder.group({
      institution: [''],
      serviceContract: ['']
    });

    this.fields = {
      institution: {
        label: 'Nº Instituição',
        items: [],
        controlName: 'institution'
      },
      serviceContract: {
        label: 'Service Contract',
        items: [],
        controlName: 'serviceContract'
      }
    };
  }

  addEconomicalGroup() {
    const dialogRef = this.modal.open(AddEconomicalGroupComponent, {
      width: '40%',
      height: '85%',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => this.getAllGroups(), 200);
    });
  }

  changeSelectedInstitution(institution: string) {
    this.selectedInstitution = institution;
  }
}
